import React, { useEffect, useState } from 'react';
import { PageSkeleton } from "../components/PageSkeleton";
import { Link, useParams } from 'react-router-dom';
import { Colors } from '../utils/utils.colors';
import { randomNumber, truncateText } from '../utils/utils.fucntions';
import { SocialShare } from '../components/subcomponents/Sharesocial';
import { Hrseparator } from '../components/subcomponents/Hrseparator';
import { ContactLink } from '../components/subcomponents/ContactComponent';
import { ProfileCard } from '../components/subcomponents/ProfileCard';
import { PageHero } from '../components/subcomponents/PageHero';
import { PageSectionHeader } from '../components/subcomponents/PageSectionHeader';
import { apiGet } from '../utils/api';
import { NotFound } from './NotFound';
import { routes } from '../utils/utils.routes';
import heroImage from '../assets/images/hero-image.png';

const parseHtmlWithHeadings = (html: string) => {
    if (!html) return html;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const elements: React.ReactNode[] = [];
    let currentSection: React.ReactNode[] = [];
    let currentHeading: { level: number; text: string } | null = null;

    const flushSection = () => {
        if (currentHeading && currentSection.length > 0) {
            elements.push(
                <section className="detail-section" key={`section-${elements.length}`}>
                    <PageSectionHeader
                        eyebrow={currentHeading.text}
                        title={currentHeading.text}
                        size="compact"
                        align="start"
                    />
                    <div className="prose lead-">
                        {currentSection}
                    </div>
                </section>
            );
        }
        currentSection = [];
        currentHeading = null;
    };

    const processNode = (node: Node): React.ReactNode => {
        // Traitement des nœuds texte
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || '';
            if (text.trim()) {
                // Vérifier si le texte n'est pas juste des espaces
                return text;
            }
            return null;
        }

        // Traitement des éléments
        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            const tagName = element.tagName.toLowerCase();
            const children = Array.from(element.childNodes).map(processNode);

            // Gestion des headings (h1, h2, h3, h4, h5, h6)
            if (/^h[1-6]$/.test(tagName)) {
                const headingText = element.textContent || '';
                if (headingText.trim()) {
                    // Sauvegarder la section précédente
                    flushSection();
                    // Créer une nouvelle section avec ce heading
                    currentHeading = { level: parseInt(tagName[1]), text: headingText };
                }
                return null;
            }

            // Gestion des listes
            if (tagName === 'ul' || tagName === 'ol') {
                const listClass = tagName === 'ul' ? 'detail-section__list' : 'detail-section__list detail-section__list--ordered';
                return React.createElement(
                    tagName,
                    { className: listClass, key: `list-${Math.random()}` },
                    children
                );
            }

            // Gestion des list items
            if (tagName === 'li') {
                return React.createElement('li', { key: `li-${Math.random()}` }, children);
            }

            // Gestion des paragraphes
            if (tagName === 'p') {
                // Vérifier si le paragraphe ne contient pas déjà des headings
                const hasHeading = Array.from(element.querySelectorAll('h1,h2,h3,h4,h5,h6')).length > 0;
                if (!hasHeading && children.length > 0) {
                    return React.createElement('p', { key: `p-${Math.random()}`, className: 'mb-3' }, children);
                }
                return children;
            }

            // Gestion des blockquotes
            if (tagName === 'blockquote') {
                return React.createElement(
                    'blockquote',
                    { className: 'detail-section__blockquote', key: `blockquote-${Math.random()}` },
                    children
                );
            }

            // Gestion des autres balises (strong, em, etc.)
            if (['strong', 'em', 'b', 'i', 'span', 'a', 'br', 'div'].includes(tagName)) {
                return React.createElement(tagName, { key: `inline-${Math.random()}` }, children);
            }

            // Pour les autres balises, on rend juste les enfants
            return children;
        }

        return null;
    };

    // Parcourir tous les nœuds enfants
    Array.from(tempDiv.childNodes).forEach(node => {
        const processed = processNode(node);
        if (processed !== null) {
            if (currentHeading !== null) {
                currentSection.push(processed);
            } else if (!currentHeading && elements.length === 0 && !currentSection.length) {
                // Si pas de heading, on ajoute directement au début
                elements.push(
                    <div className="prose lead-" key={`content-start`}>
                        {processed}
                    </div>
                );
            } else if (!currentHeading && currentSection.length === 0) {
                currentSection.push(processed);
            }
        }
    });

    // Ne pas oublier la dernière section
    flushSection();

    return elements;
};

export const Center: React.FC = () => {
    const { center } = useParams<{ center: string }>();
    const [item, setItem] = useState<any>(undefined);
    const [otherCenters, setOtherCenters] = useState<any[]>([]);
    const [articles, setArticles] = useState<any[]>([]);

    useEffect(() => {
        if (!center) return;
        apiGet(`/centers/slug/${center}`)
            .then(async (d) => {
                setItem(d.item);
                if (d.item?.id) {
                    const list = await apiGet('/contents', { centerId: d.item.id, type: 'article', limit: 50 });
                    setArticles(list.rows || []);
                }
            })
            .catch(() => setItem(null));
        apiGet(`/centers`)
            .then((d) => setOtherCenters((d.rows || []).filter((c: any) => c.slug !== center)))
            .catch(() => { });
    }, [center]);

    if (item === undefined) return <PageSkeleton />;
    if (item === null) return <NotFound />;

    const { title, description, domaineInterventions, images, contacts, etudesRealisees, partenaires, slug: flug, profile, direction } = item;

    const cover = images?.[0] || item.coverImage || heroImage;

    const parsedProfileContent = profile ? parseHtmlWithHeadings(profile) : null;

    return (
        <>
            <PageHero
                imageCover={cover}
                eyebrow="Centre de recherche"
                title={title ?? ""}
                lead={truncateText(description ?? "", 200)}
                breadcrumbs={[
                    { label: "Accueil", href: routes.HOME },
                    { label: "Centres de recherche", href: routes.CENTRES },
                    { label: title ?? "" },
                ]}
            />
            <div className="page-content padding-x is-layout-constrained py-4">
                <div className="row">
                    <aside className="col-12 col-md-4 pe-md-5 order-last order-md-first">
                        {direction?.image && (
                            <ProfileCard
                                profile={direction ?? {
                                    slug: "",
                                    name: "ULPGL",
                                    role: "Direction",
                                    uuid: randomNumber(10).toString()
                                }}
                                showDescription={false}
                                variant="sidebar"
                            />
                        )}

                        <div className="content-detail__card">
                            <h3>Partage</h3>
                            <SocialShare slug={flug ?? ""} title={title ?? ""} description={description ?? ""} />
                        </div>

                        {otherCenters.length > 0 && (
                            <div className="mt-5 pt-5">
                                <PageSectionHeader
                                    eyebrow="Explorer"
                                    title="Autres centres de recherche"
                                    size="compact"
                                    align="start"
                                />
                                <ul className="list-unstyled detail-section__list">
                                    {otherCenters.map((c: any) => (
                                        <li key={c.slug} className="mb-2">
                                            <Link to={`${routes.CENTRES}/${c.slug}`} className="text-decoration-none text-dark">
                                                {c.title}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </aside>

                    <main className="col-12 col-md-8 order-first order-md-last">
                        <article className="entry-content detail-sections">
                            <div
                                className="prose lead-"
                                dangerouslySetInnerHTML={{ __html: description ?? "" }}
                            />

                            {images?.length > 0 && (
                                <div className="row g-3 my-4">
                                    {images.map((image: string, idx: number) => (
                                        <div className="col-6 col-md-4" key={idx}>
                                            <img src={image} className="img-fluid rounded" alt="" />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {profile && (
                                <section className="detail-section">
                                    <PageSectionHeader
                                        eyebrow="Présentation"
                                        title="Profil"
                                        size="compact"
                                        align="start"
                                    />
                                    <div className="prose lead-">
                                        {parsedProfileContent}
                                    </div>
                                </section>
                            )}

                            {domaineInterventions?.length > 0 && (
                                <section className="detail-section">
                                    <PageSectionHeader
                                        eyebrow="Expertise"
                                        title="Domaines d'intervention"
                                        size="compact"
                                        align="start"
                                    />
                                    <ul className="detail-section__list">
                                        {domaineInterventions.map((domaine: string, idx: number) => (
                                            <li key={idx}>{domaine}</li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {etudesRealisees?.length > 0 && (
                                <section className="detail-section">
                                    <PageSectionHeader
                                        eyebrow="Recherche"
                                        title="Études réalisées"
                                        size="compact"
                                        align="start"
                                    />
                                    <ul className="detail-section__list">
                                        {etudesRealisees.map((etude: string, idx: number) => (
                                            <li key={idx}>{etude}</li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {partenaires?.length > 0 && (
                                <section className="detail-section">
                                    <PageSectionHeader
                                        eyebrow="Réseau"
                                        title="Partenaires"
                                        size="compact"
                                        align="start"
                                    />
                                    <ul className="detail-section__list">
                                        {partenaires.map((partenaire: string, idx: number) => (
                                            <li key={idx}>{partenaire}</li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {direction?.name && (
                                <section className="detail-section">
                                    <PageSectionHeader
                                        eyebrow="Équipe"
                                        title="Direction"
                                        size="compact"
                                        align="start"
                                    />
                                    <p className="detail-section__highlight">{direction.name}</p>
                                    {direction.description && (
                                        <div
                                            className="prose lead-"
                                            dangerouslySetInnerHTML={{
                                                __html: `<strong>Mot de la direction : </strong>${direction.description}`,
                                            }}
                                        />
                                    )}
                                </section>
                            )}

                            {contacts?.length > 0 && (
                                <section className="detail-section">
                                    <PageSectionHeader
                                        eyebrow="Contact"
                                        title="Contacts du centre"
                                        size="compact"
                                        align="start"
                                    />
                                    <ul className="detail-section__list">
                                        {contacts.map((contact: string, idx: number) => (
                                            <li key={idx}>
                                                <ContactLink value={contact} />
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {articles.length > 0 && (
                                <section className="detail-section" data-testid="center-articles">
                                    <PageSectionHeader
                                        eyebrow="Actualités"
                                        title="Publications du centre"
                                        size="compact"
                                        align="start"
                                    />
                                    <div style={{ display: 'grid', gap: 12 }}>
                                        {articles.map((c: any) => (
                                            <Link
                                                key={c.id}
                                                to={`${routes.ARTICLESDETAILS}/${c.slug}`}
                                                style={{
                                                    display: 'flex', gap: 16, background: '#fff', padding: 16, borderRadius: 12,
                                                    textDecoration: 'none', color: 'inherit', border: '1px solid #e8ecf1',
                                                }}
                                            >
                                                {c.coverImage && (
                                                    <img src={c.coverImage} alt="" style={{ width: 120, height: 88, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                                                )}
                                                <div>
                                                    <h5 style={{ margin: '0 0 4px', color: Colors.darkColor, fontSize: 16 }}>{c.title}</h5>
                                                    <p style={{ fontSize: 13, color: '#666', margin: 0 }}>{(c.excerpt || '').slice(0, 140)}</p>
                                                    <span style={{ fontSize: 12, color: '#999', marginTop: 6, display: 'inline-block' }}>
                                                        {new Date(c.publishedAt || c.createdAt).toLocaleDateString('fr-FR')}
                                                    </span>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </article>
                    </main>
                </div>
                <Hrseparator />
            </div>
        </>
    );
};